import { useLocalStorage } from "@hooks/useLocalStorage";
import { createContext, ReactNode, useCallback, useContext, useEffect } from "react";

type Hub = {
	id: string;
	name: string;
	// Scanned side
	side: "A" | "B" | undefined;
};

type HubSession = {
	hub: Hub;
	timestamp: number;
};

type HubSessionContextType = {
	hub: Hub | null;
	setHub: (hub: Hub) => void;
	clearHub: () => void;
};

const HubSessionContext = createContext<HubSessionContextType | undefined>(undefined);

const SESSION_DURATION_MS = 5 * 60 * 60 * 1000; // 5 hours

type HubSessionProviderProps = {
	children: ReactNode;
};

export const HubSessionProvider = ({ children }: HubSessionProviderProps) => {
	const [hubSession, setHubSession] = useLocalStorage<HubSession | null>({
		key: "hub-session",
		initialValue: null,
	});

	const setHub = useCallback(
		(hub: Hub) => {
			setHubSession({
				hub,
				timestamp: Date.now(),
			});
		},
		[setHubSession]
	);
	const clearHub = useCallback(() => setHubSession(null), [setHubSession]);

	useEffect(() => {
		if (!hubSession) {
			return;
		}

		const timeoutDuration = Math.max(0, SESSION_DURATION_MS - (Date.now() - hubSession.timestamp));
		const timeoutId = setTimeout(() => {
			setHubSession(null);
		}, timeoutDuration);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [hubSession, setHubSession]);

	return (
		<HubSessionContext.Provider
			value={{
				hub: hubSession?.hub ?? null,
				setHub,
				clearHub,
			}}
		>
			{children}
		</HubSessionContext.Provider>
	);
};

// TODO: Rename it to scannedHub from hub
export const useHubSession = () => {
	const context = useContext(HubSessionContext);
	if (context === undefined) {
		throw new Error("useHubSession must be used within a HubSessionProvider");
	}
	return context;
};
